import clsx from "clsx";
import { useGetReportSummary, useLocalSampleSummaryParams } from "../../hooks";
import { FileSimilarity } from "../common";
import { Actions, LayoutActions, NtiLink, ReanalyzeButton } from "./components";

import { RenderWhen } from "../../../../components";
import { enums } from "../../../../shim/jsbridge";
import { isValidHttpUrl } from "../../../../utils";
import SampleSummaryBreadcrumb from "../common/summarybreadcrumb/SampleSummaryBreadcrumb";
import styles from "./topheader.module.scss";

const {
  analyzer: { URLANALYZER }
} = enums ?? {};

// This is here for the purpose of refetching data in SampleSummaryBasic.jsx
// once that is moved to functional component and api calls are moved to TanstackQuery,
// this can be removed as it can be refetched using useQueryClient
type Props = {
  onClassificationChange?: () => void;
  onReanalyze?: (payload: unknown) => void;
};

const ReportSummaryTopHeader = ({
  onClassificationChange,
  onReanalyze
}: Props) => {
  const { hash, sourceId, relationshipId } = useLocalSampleSummaryParams();
  const { data } = useGetReportSummary(hash, sourceId, relationshipId);

  if (!data) return null;

  const isNtiValue =
    isValidHttpUrl(data.file_name) || isValidHttpUrl(data.proposed_filename);
  const showNtiLink = isNtiValue || data.analyzer === URLANALYZER;

  return (
    <header
      className={styles.container}
      data-testid="report_summary_top_header"
    >
      <div className={clsx(styles.header_title, "overflow-ellipsis")}>
        {sourceId && hash && (
          <SampleSummaryBreadcrumb
            sourceFilename={data.source_filename}
            filename={data.file_name}
            relationshipId={relationshipId === 0 ? "" : relationshipId}
            sourceId={sourceId}
            sourceSha1={data.source_sha1}
            className={styles.nav_breadcrumb}
          />
        )}
      </div>
      <div
        className="flex flex-gap-2 align-center k-pdf-hide-in-pdf"
        id={styles.action_buttons}
      >
        <RenderWhen condition={showNtiLink}>
          <NtiLink
            className="m-right-4"
            fileName={data.file_name}
            proposedFileName={data.proposed_filename}
          />
        </RenderWhen>
        <ReanalyzeButton onReanalyze={onReanalyze} />
        <FileSimilarity
          label="SIMILARITY"
          hasRha={Boolean(data.threat_intelligence?.rha)}
          sha1={data.sha1 ?? ""}
          fileSize={data.file_size}
          fileType={data.file_type}
          threatName={data.threat_name}
        />
        <Actions
          fileName={data.file_name}
          extractedFileCount={data.extracted_file_count}
          sourceSha1={data.is_extracted_file ? data.source_sha1 : null}
          sourceFileName={data.source_filename}
          uploader={data.uploader}
          onClassificationChange={onClassificationChange}
        />
        <LayoutActions />
      </div>
    </header>
  );
};

export default ReportSummaryTopHeader;
